import React, { useCallback, useMemo } from 'react'
import { Col, Row } from 'react-bootstrap'
import { State, useState } from '@hookstate/core'
import { RefCountry } from '../../globalStates/ReferenceDataState'
import { useLanguageState } from '../../globalStates/LanguageState'
import { Organization, PrintCatalog, countryHasZipCode } from '../../globalStates/ExhibitorDataState'
import { ReferenceDataType, withReferenceDataState } from '../../globalStates/ReferenceDataHOC'
import { getElementFieldValue, makeSetHandler, makeValidationHandler } from '../../globalStates/utils'
import Section from '../../components/Section'
import TextField from '../../components/Field/Text'
import SelectField from '../../components/Field/Select'
import ToggleSwitchField from '../../components/Field/ToggleSwitchField'

function buildCountryOptions(refCountries: Array<RefCountry>) {
    return refCountries.map(refCountry => ({ id: refCountry.value, label: refCountry.name, value: refCountry.value }))
}

interface AddressSectionProps {
    printCatalogState: State<PrintCatalog>
    organizationState: State<Organization>
    readOnly: boolean
}

const AddressSection: React.FC<AddressSectionProps & ReferenceDataType> = ({ printCatalogState, organizationState, refDataState, readOnly }) => {
    const branding = useLanguageState().getStrings()
    const printCatalog = useState(printCatalogState)
    const countries = useMemo(() => buildCountryOptions(refDataState.getCountries()), [refDataState])
    const onChangeAddressImported = useCallback(() => {
        printCatalog.addressImported.set(!printCatalog.addressImported.get())
        if (printCatalog.addressImported.get()) {
            printCatalog.address1.set(organizationState.address1.get())
            printCatalog.address2.set(organizationState.address2.get())
            printCatalog.address3.set(organizationState.address3.get())
            printCatalog.postcode.set(organizationState.postCode.get())
            printCatalog.city.set(organizationState.city.get())
            printCatalog.countryCode.set(organizationState.countryCode.get())
        }
    }, [organizationState, printCatalog])

    return (
        <Section title={branding.configuration.printPage.sctAddressPrintCatalogue.lblSectionAddressInformation}>            <Row>
            <Col>
                <ToggleSwitchField
                    name="copyCompanyAddress"
                    label={branding.configuration.printPage.lblCopyData}
                    value={printCatalog.addressImported.get()}
                    readonly={readOnly}
                    onChange={onChangeAddressImported}
                />
            </Col>
        </Row>
            <Row>
                <Col>
                    <TextField
                        id="address1"
                        name="address1"
                        label={branding.configuration.printPage.sctAddressPrintCatalogue.lblAddress1}
                        placeholder={branding.configuration.printPage.sctAddressPrintCatalogue.hntAddress1}
                        validate={makeValidationHandler(printCatalog, 'address1')}
                        value={getElementFieldValue(printCatalog, "address1")}
                        onChange={makeSetHandler(printCatalog, 'address1')}
                        required
                        readonly={printCatalog.addressImported.get() || readOnly}
                        limit={100}
                    />
                </Col>
                <Col>
                    <TextField
                        id="address2"
                        name="address2"
                        label={branding.configuration.printPage.sctAddressPrintCatalogue.lblAddress2}
                        placeholder={branding.configuration.printPage.sctAddressPrintCatalogue.hntAddress2}
                        value={getElementFieldValue(printCatalog, "address2")}
                        onChange={makeSetHandler(printCatalog, 'address2')}
                        readonly={printCatalog.addressImported.get() || readOnly}
                        limit={100}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <TextField
                        id="adress3"
                        name="adress3"
                        label={branding.configuration.printPage.sctAddressPrintCatalogue.lblAddress3}
                        placeholder={branding.configuration.printPage.sctAddressPrintCatalogue.hntAddress3}
                        value={getElementFieldValue(printCatalog, "address3")}
                        onChange={makeSetHandler(printCatalog, 'address3')}
                        readonly={printCatalog.addressImported.get() || readOnly}
                        limit={100}
                    />
                </Col>
                <Col>
                    <TextField
                        id="postcode"
                        name="postcode"
                        label={branding.configuration.printPage.sctAddressPrintCatalogue.lblZipCode}
                        placeholder={branding.configuration.printPage.sctAddressPrintCatalogue.hntZipCode}
                        validate={makeValidationHandler(printCatalog, 'postcode')}
                        value={getElementFieldValue(printCatalog, "postcode")}
                        onChange={makeSetHandler(printCatalog, 'postcode')}
                        readonly={printCatalog.addressImported.get() || readOnly}
                        required={countryHasZipCode(getElementFieldValue(printCatalog, 'countryCode'))}
                        limit={50}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <TextField
                        id="city"
                        name="city"
                        label={branding.configuration.printPage.sctAddressPrintCatalogue.lblCity}
                        placeholder={branding.configuration.printPage.sctAddressPrintCatalogue.hntCity}
                        validate={makeValidationHandler(printCatalog, 'city')}
                        value={getElementFieldValue(printCatalog, "city")}
                        onChange={makeSetHandler(printCatalog, 'city')}
                        readonly={printCatalog.addressImported.get() || readOnly}
                        required
                        limit={100}
                    />
                </Col>
                <Col>
                    <SelectField
                        id="country"
                        name="country"
                        label={branding.configuration.printPage.sctAddressPrintCatalogue.lblCountry}
                        placeholder={branding.configuration.printPage.sctAddressPrintCatalogue.hntCountry}
                        options={countries}
                        required
                        validate={makeValidationHandler(printCatalog, 'countryCode')}
                        value={getElementFieldValue(printCatalog, "countryCode")}
                        onChange={makeSetHandler(printCatalog, 'countryCode')}
                        readonly={printCatalog.addressImported.get() || readOnly}
                    />
                </Col>
            </Row>
        </Section>
    );
}

export default React.memo(withReferenceDataState(AddressSection))
